/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/es'
import { useLocation, useNavigate } from 'react-router-dom'
import { MainLayout } from 'views/layouts/'
import {
  Container,
  RowContainer,
  InfoUserContainer,
  ReportContainer,
  Subtitle,
  WrapperInfo,
  InfoText,
  CardInfoWithBorder,
  RowTextWrapper,
  ScoreSectionWrapper,
  ScoreWrapperInfo,
  BackButtonWrapper,
  ButtonContainer,
  CompetencesContainer,
  CompetencesList,
  CompetenceItem,
  ActionContainer,
  StateButton,
} from './styled'
import { H2, H4 } from 'views/components/UI/Text'
import { StrokedButton, BackButton } from 'views/components/UI/Buttons'
import CardDownloadResults from 'views/components/Cards/DownloadResults'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { mockPeopleToEvaluate } from 'views/screens/Evaluated-User/pages/QuestionsTest360/people-mock'
import { HEADERS_USERS_TEST_360 } from './constants'
import BluesiteTableComponent from 'views/components/UI/BluesiteTable'
import ModalCloseEvaluation from './modal-close-evaluation'
import { fetchResults360, Test360ResponseTypes } from 'views/screens/Evaluated-User/services/fetch-results-360'
import getTest360Report from 'services/pdfReports/getTest360Report'
import { useNotification } from 'lib/context/notification.context'
import { t } from 'i18next'
import sendReminderTestToUser from 'services/common/reports/postReminderTestToUser/postReminderTestToUser'
import { isDateMoreThan24HoursOld } from 'utils/formatDate'
import TooltipComponent from 'views/components/Tooltip'
import { closeEvaluation360 } from 'views/screens/Evaluated-User/services/close-evaluation-360'

const Results360 = () => {
  const navigate = useNavigate()
  const location: any = useLocation()
  dayjs.extend(utc)

  const userTestId = location.state?.userTestId

  const { getError, getSuccess, getWarning } = useNotification()

  const [testData, setTestData] = useState<Test360ResponseTypes | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [loadingReport, setLoadingReport] = useState<boolean>(false)
  const [participation, setParticipation] = useState<boolean>(false)
  const [isSendingReport, setIsSendingReport] = useState<boolean>(false)
  const [disabledButton, setDisabledButton] = useState<boolean>(false)
  const [disablePlanButton, setDisablePlanButton] = useState<boolean>(false)
  const [pageUsers, setPageUsers] = useState<number>(1)
  const [showModalCloseEvaluation, setShowModalCloseEvaluation] = useState<boolean>(false)
  const [isSendingReminder, setIsSendingReminder] = useState<boolean>(false)
  const [reminderSentSuccessfully, setReminderSentSuccessfully] = useState<boolean>(false)
  const [lastReminderSentTime, setLastReminderSentTime] = useState<string | null>(null)
  const [testToClose, setTestToClose] = useState<{ userTestId: number; evaluatorUserTestId: number } | null>(null)
  const [closingEvaluation, setClosingEvaluation] = useState<boolean>(false)

  useEffect(() => {
    if (!userTestId) return
    setLoading(true)

    fetchResults360(userTestId)
      .then((res) => {
        setTestData(res)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [userTestId])

  const handleDownloadReport = async () => {
    try {
      setLoadingReport(true)
      const response = await getTest360Report(userTestId)

      const { url } = response

      window.open(url, '_blank')

      getSuccess(t('global.notifications.reportGenerated'))
      setLoadingReport(false)
    } catch (error) {
      console.error('Error al descargar el reporte de 360°:', error)
      setLoadingReport(false)
    }
  }

  const evaluatedCompleted =
    testData?.evaluator_details?.find((evaluator) => evaluator.role_name === 'Evaluado')?.status === 'completed'

  // Obtener evaluadores con status "pending" o "started"
  const pendingEvaluators =
    testData?.evaluator_details?.filter(
      (evaluator) =>
        evaluator.role_name === 'Evaluado' && (evaluator.status === 'pending' || evaluator.status === 'started')
    ) || []

  // Si no hay evaluadores pending, deshabilitar el botón
  const hasPendingEvaluators = pendingEvaluators.length > 0

  // Si hay evaluadores pending, verificar si alguno puede recibir recordatorio (más de 24 horas desde el último)
  // Si se envió un recordatorio recientemente (lastReminderSentTime), usarlo para la validación
  const canSendReminder = lastReminderSentTime
    ? isDateMoreThan24HoursOld(lastReminderSentTime)
    : pendingEvaluators.some((evaluator) => isDateMoreThan24HoursOld(evaluator.test_reminder))

  const disableReminderButton = evaluatedCompleted || !canSendReminder

  const handleSendReminder = async () => {
    const email = {
      emails: [{ email: testData?.header_data.user_email }],
    }
    const processId = testData?.header_data?.process_id

    setIsSendingReminder(true)

    try {
      const dataResponse = await sendReminderTestToUser(email, processId)
      if (dataResponse.status === 'OK') {
        getSuccess('Recordatorio enviado exitosamente')
        setReminderSentSuccessfully(true)
        // Guardar el tiempo actual como última vez que se envió el recordatorio
        setLastReminderSentTime(new Date().toISOString())
      }
    } catch (error) {
      getError('Ha ocurrido un error al enviar el recordatorio')
    } finally {
      setIsSendingReminder(false)
    }
  }

  const handleDisabledClick = () => {
    if (!hasPendingEvaluators) {
      getWarning('Ya se han completado todas las evaluaciones')
    } else if (!canSendReminder) {
      getWarning('Debes esperar 24 horas para volver a enviar el recordatorio')
    }
  }

  const handleDownloadReportDisabled = () => {
    getWarning(
      'Debes esperar a que tanto evaluadores como evaluado hayan completado la prueba para descargar el reporte'
    )
  }

  const handleCloseEvaluation360 = async () => {
    if (!testToClose) return
    try {
      setClosingEvaluation(true)
      const response = await closeEvaluation360(testToClose.userTestId, testToClose.evaluatorUserTestId)
      if (response.status === 'OK') {
        setShowModalCloseEvaluation(false)
        getSuccess('Evaluación cerrada exitosamente')
        setTestToClose(null)
        setClosingEvaluation(false)

        // Recargar los datos para actualizar la tabla
        fetchResults360(userTestId)
          .then((res) => {
            setTestData(res)
          })
          .catch(() => {
            // En caso de error al recargar, mantener los datos existentes
            console.error('Error al recargar los datos después del cierre')
          })
      } else {
        setClosingEvaluation(false)
        setTestToClose(null)
        getError('Ha ocurrido un error al cerrar la evaluación')
      }
    } catch (error) {
      getError('Ha ocurrido un error al cerrar la evaluación')
      setTestToClose(null)
      setClosingEvaluation(false)
    }
  }

  const handleCancelModalClose = () => {
    setShowModalCloseEvaluation(false)
    setTestToClose(null)
  }

  const CloseEvaluationComponent = (item: any) => {
    const disableButton = item.state === 'closed' || item.state === 'completed' || item.role_name === 'Autoevaluación'

    const handleClickClose = () => {
      // Validar si este es el último evaluador disponible y todos los demás están cerrados
      const availableEvaluators =
        testData?.evaluator_details?.filter(
          (evaluator) => evaluator.role_name !== 'Evaluado' && evaluator.status !== 'closed'
        ) || []

      // Si solo queda este evaluador disponible, no permitir cerrarlo
      if (
        availableEvaluators.length === 1 &&
        availableEvaluators[0].evaluator_user_test_id === item.evaluator_user_test_id
      ) {
        getWarning('Al menos un evaluador debe responder la prueba')
        return
      }

      setTestToClose({ userTestId: userTestId, evaluatorUserTestId: item.evaluator_user_test_id })
      setShowModalCloseEvaluation(true)
    }
    return (
      <>
        {item.role_name !== 'Autoevaluación' ? (
          <ActionContainer>
            <StrokedButton onClick={handleClickClose} disabled={disableButton} style={{ margin: '0' }}>
              Cerrar evaluación
            </StrokedButton>
          </ActionContainer>
        ) : null}
      </>
    )
  }

  const EvaluationStateComponent = (item: any) => {
    const getStateConfig = (state: string) => {
      switch (state) {
        case 'completed':
          return { text: 'Finalizado', backgroundColor: '#84E6B7', color: '#000000' }
        case 'started':
          return { text: 'En proceso', backgroundColor: '#FAE0A8', color: '#000000' }
        case 'closed':
          return { text: 'Cerrado', backgroundColor: '#E69090', color: '#000000' }
        case 'pending':
        default:
          return { text: 'Sin iniciar', backgroundColor: '#CFCFCF', color: '#000000' }
      }
    }

    const config = getStateConfig(item.state)

    return (
      <ActionContainer>
        <StateButton backgroundColor={config.backgroundColor} color={config.color}>
          {config.text}
        </StateButton>
      </ActionContainer>
    )
  }

  const modifiedEvaluators =
    testData?.evaluator_details
      .map((person) => ({
        ...person,
        role_name: person.role_name === 'Evaluado' ? 'Autoevaluación' : person.role_name,
        state: person.status,
        status: <EvaluationStateComponent state={person.status} />,
      }))
      .sort((a, b) => {
        if (a.role_name === 'Autoevaluación') return -1
        if (b.role_name === 'Autoevaluación') return 1
        return 0
      }) || []

  const disableReportButton = modifiedEvaluators.every(
    (evaluator) => evaluator.state === 'completed' || evaluator.state === 'closed'
  )

  return (
    <MainLayout>
      {!loading ? (
        <Container>
          <RowContainer justifyContent="space-between" margin="0 0 20px 0">
            <H2>Evaluación 360° - {testData?.report_title}</H2>
            <div>
              <TooltipComponent
                tooltipText="Una vez que envíes el recordatorio, deberás esperar 24 horas para volver a enviarlo"
                placement="bottom"
                iconColor="#2D749C"
                backgroundColor="#2D749C"
              />
              <StrokedButton
                onClick={handleSendReminder}
                onDisabledClick={handleDisabledClick}
                disabled={disableReminderButton || isSendingReminder}
              >
                {isSendingReminder ? 'Enviando...' : 'Enviar recordatorio'}
              </StrokedButton>
            </div>
          </RowContainer>
          <RowContainer>
            <WrapperInfo>
              <H4>Datos de la Prueba</H4>
              <RowContainer>
                <Subtitle>Empresa:</Subtitle>
                <InfoText>{testData?.header_data?.company_name}</InfoText>

                <Subtitle>Nombre del Proceso:</Subtitle>
                <InfoText>{testData?.header_data?.process_name}</InfoText>

                <Subtitle>Fecha de realización:</Subtitle>
                <InfoText>{testData?.header_data?.completion_date}</InfoText>
              </RowContainer>
            </WrapperInfo>
          </RowContainer>
          <InfoUserContainer>
            <ScoreSectionWrapper>
              <ScoreWrapperInfo>
                <CardInfoWithBorder>
                  <RowTextWrapper>
                    <Subtitle style={{ marginRight: '8px' }}>Puntaje con autoevaluación: </Subtitle>{' '}
                    <InfoText>{testData?.scores?.score_with_autoevaluation}</InfoText>
                  </RowTextWrapper>
                  <RowTextWrapper>
                    <Subtitle style={{ marginRight: '8px' }}>Puntaje sin autoevaluación: </Subtitle>{' '}
                    <InfoText>{testData?.scores?.score_without_autoevaluation}</InfoText>
                  </RowTextWrapper>
                  <RowTextWrapper>
                    <Subtitle style={{ marginRight: '8px' }}>Valor de disonancia: </Subtitle>{' '}
                    {typeof testData?.scores?.dissonance_value === 'number' ? (
                      <InfoText>
                        {testData?.scores?.dissonance_value} - "{testData?.scores?.dissonance_text}"
                      </InfoText>
                    ) : null}
                  </RowTextWrapper>
                  <CompetencesContainer>
                    <Subtitle>Lista de competencias:</Subtitle>
                    <CompetencesList>
                      {testData?.competencies.map((competence, index) => (
                        <CompetenceItem key={index}>{competence}</CompetenceItem>
                      ))}
                    </CompetencesList>
                  </CompetencesContainer>
                </CardInfoWithBorder>
              </ScoreWrapperInfo>
              <ButtonContainer>
                <ReportContainer>
                  <CardDownloadResults
                    title="Evaluación 360°"
                    type="360"
                    onClick={handleDownloadReport}
                    user_test_id={userTestId}
                    planToDownload={disablePlanButton}
                    setLoadingReport={setLoadingReport}
                    disabled={!disableReportButton}
                    onClickDisabled={handleDownloadReportDisabled}
                  />
                </ReportContainer>
              </ButtonContainer>
            </ScoreSectionWrapper>
          </InfoUserContainer>
          <BluesiteTableComponent
            ActionComponent={CloseEvaluationComponent}
            checks={false}
            data={modifiedEvaluators}
            headers={HEADERS_USERS_TEST_360}
            searchBar={false}
            searchLabel=""
            currentPage={pageUsers}
            totalPages={Math.ceil(mockPeopleToEvaluate.length / 10)}
            handlePagination={setPageUsers}
            showNumbers={true}
          />
          <ModalCloseEvaluation
            showModal={showModalCloseEvaluation}
            handleClose={handleCancelModalClose}
            handleCloseEvaluation={handleCloseEvaluation360}
          />
        </Container>
      ) : (
        <ModalLoader isLoading={loading} />
      )}
      <ModalLoader isLoading={closingEvaluation} />
      <BackButtonWrapper>
        <BackButton onClick={() => navigate(-1)} />
      </BackButtonWrapper>
      <ModalLoader isLoading={loadingReport} fontWeight={600} margin="20px 0" text="Estamos generando el archivo..." />
    </MainLayout>
  )
}

export default Results360
